import { Button, Switch, Tooltip } from '@heroui/react';
import {
  AlignCenterHorizontal,
  AlignCenterVertical,
  AlignEndHorizontal,
  AlignEndVertical,
  AlignStartHorizontal,
  AlignStartVertical,
} from 'lucide-react';
import React, { FC } from 'react';

import { useSceneEditing } from '../../hooks';
import { AdElement, AdScene, Shadow } from '../../model/types';
import { ColorField, NumberField, PanelSection, SliderField } from '../common';

interface CommonPropertiesProps {
  element: AdElement;
  scene: AdScene;
}

const DEFAULT_SHADOW = { color: '#000000', blur: 24, offsetX: 0, offsetY: 12 };

type AlignAction = 'left' | 'centerX' | 'right' | 'top' | 'centerY' | 'bottom';

const ALIGN_ACTIONS: { action: AlignAction; icon: typeof AlignStartVertical; label: string }[] = [
  { action: 'left', icon: AlignStartVertical, label: 'Do lewej krawędzi' },
  { action: 'centerX', icon: AlignCenterVertical, label: 'Wyśrodkuj w poziomie' },
  { action: 'right', icon: AlignEndVertical, label: 'Do prawej krawędzi' },
  { action: 'top', icon: AlignStartHorizontal, label: 'Do górnej krawędzi' },
  { action: 'centerY', icon: AlignCenterHorizontal, label: 'Wyśrodkuj w pionie' },
  { action: 'bottom', icon: AlignEndHorizontal, label: 'Do dolnej krawędzi' },
];

interface ShadowFieldsProps {
  elementId: string;
  shadow: Shadow;
}

const ShadowFields: FC<ShadowFieldsProps> = ({ elementId, shadow }) => {
  const { patchElement } = useSceneEditing();

  return (
    <>
      <ColorField
        label="Kolor cienia"
        value={shadow.color}
        onChange={(color) => patchElement(elementId, { shadow: { ...shadow, color } }, `shadow-color-${elementId}`)}
      />
      <SliderField
        label="Rozmycie"
        suffix=" px"
        min={0}
        max={120}
        value={shadow.blur}
        onChange={(blur) => patchElement(elementId, { shadow: { ...shadow, blur } }, `shadow-blur-${elementId}`)}
      />
      <SliderField
        label="Przesunięcie w pionie"
        suffix=" px"
        min={-80}
        max={80}
        value={shadow.offsetY}
        onChange={(offsetY) => patchElement(elementId, { shadow: { ...shadow, offsetY } }, `shadow-y-${elementId}`)}
      />
    </>
  );
};

export const CommonProperties: FC<CommonPropertiesProps> = ({ element, scene }) => {
  const { patchElement } = useSceneEditing();

  const align = (action: AlignAction): void => {
    const positions: Record<AlignAction, Partial<AdElement>> = {
      left: { x: 0 },
      centerX: { x: Math.round((scene.width - element.width) / 2) },
      right: { x: Math.round(scene.width - element.width) },
      top: { y: 0 },
      centerY: { y: Math.round((scene.height - element.height) / 2) },
      bottom: { y: Math.round(scene.height - element.height) },
    };
    patchElement(element.id, positions[action]);
  };

  return (
    <>
      <PanelSection title="Wyrównanie do planszy">
        <div className="flex flex-wrap gap-1">
          {ALIGN_ACTIONS.map(({ action, icon: Icon, label }) => (
            <Tooltip key={action} content={label} size="sm">
              <Button size="sm" radius="sm" isIconOnly variant="flat" aria-label={label} onPress={() => align(action)}>
                <Icon size={16} />
              </Button>
            </Tooltip>
          ))}
        </div>
      </PanelSection>

      <PanelSection title="Położenie i rozmiar">
        <div className="grid grid-cols-2 gap-2">
          <NumberField label="X" value={element.x} onChange={(x) => patchElement(element.id, { x }, `x-${element.id}`)} />
          <NumberField label="Y" value={element.y} onChange={(y) => patchElement(element.id, { y }, `y-${element.id}`)} />
          <NumberField
            label="Szerokość"
            min={8}
            value={element.width}
            onChange={(width) => patchElement(element.id, { width: Math.max(8, width) }, `w-${element.id}`)}
          />
          {element.type !== 'text' && (
            <NumberField
              label="Wysokość"
              min={8}
              value={element.height}
              onChange={(height) => patchElement(element.id, { height: Math.max(8, height) }, `h-${element.id}`)}
            />
          )}
        </div>
        <SliderField
          label="Obrót"
          suffix="°"
          min={-180}
          max={180}
          value={element.rotation}
          onChange={(rotation) => patchElement(element.id, { rotation }, `rotation-${element.id}`)}
        />
        <SliderField
          label="Krycie"
          suffix="%"
          min={0}
          max={100}
          value={Math.round(element.opacity * 100)}
          onChange={(value) => patchElement(element.id, { opacity: value / 100 }, `opacity-${element.id}`)}
        />
      </PanelSection>

      <PanelSection title="Cień">
        <Switch
          size="sm"
          isSelected={Boolean(element.shadow)}
          onValueChange={(enabled) => patchElement(element.id, { shadow: enabled ? DEFAULT_SHADOW : undefined })}
        >
          <span className="text-xs">Cień pod elementem</span>
        </Switch>
        {element.shadow && <ShadowFields elementId={element.id} shadow={element.shadow} />}
      </PanelSection>
    </>
  );
};
